import {IBody, IO, UrlQs} from "lib/commons/io/io";
import {DOM, JSX} from "lib/commons/xml/dom";
import {EventMgr} from "lib/commons/events";
import {IUniverseEnv, Universe} from "lib/core/universe";
import {IReg} from "lib/commons/registry";
import {ERROR} from "lib/core/errorReport";

export interface OCidSessionConfig {
	/** Manifest associé à cette session. */
	manifest: CidManifest

	/** Gestionnaire graphique. */
	uiHandler: ICidUiHandler;

	/** Process déjà connu et sélectionné */
	process?: CidProcess

	/** Sélection du process par la prop "is" */
	isProcess?: string[]

	/** Liste restreinte des Process dans lequel peut choisir */
	processes?: CidProcess[]

	/** Ignorer les steps d'interaction optionnelles **/
	cidPassOptionalInteraction?: boolean

	/**
	 * Métas entrantes identifées par leur name. Généralement issues d'un mémoire persistante
	 * d'un précédent déploiement de la même ressource.
	 */
	metasIn?: Dict<string>

	/**
	 * Valeurs de metas apportées et étrangères au process cible mais potentiellement
	 * utilisables par ce dernier avec un matching des is.
	 *
	 * @see constantes CID.IS_xxx définissant des "is" standards.
	 */
	foreignMetas?: { is: string[], value: string | ((session: CidSession) => string) }[]

	/**
	 * Gestion personnalisée de l'upload (ex: redirigée via notre couche serveur, pas par le client).
	 *
	 * Si retourne null, l'impl standard est réassumée avec session.config.bodyToUpload
	 * (qui a pu être affecté au cours de cet appel de méthode).
	 */
	customUpload?: (session: CidSession, uploadStep: CidUploadStep, webUpload: ICidWebUploadDef) => Promise<null | Response>

	/** Contenu à uploader à l'étape cid:upload. */
	bodyToUpload?: IBody

}

/**
 * Comportements nécessaires à implémenter par le contexte graphique.
 *
 * NON implémenté par rapport aux possibilités du protocole :
 * - Exploitation des cid:restriction
 * - Construction d'une interface automatique de saisie des champs manquants.
 */
export interface ICidUiHandler {

	/**
	 * Interface de sélection du process.
	 * @return Si null abandon et cloture de la session.
	 */
	selectProcess(session: CidSession, choices: Set<CidProcess>): Promise<CidProcess | null>

	/** Authentification par une iframe web. */
	webAuth(session: CidSession, step: CidWebAuthStep): Promise<ICidStepResult>

	/**
	 * Etape interactive par une iframe web.
	 * Le handler doit utiliser step.initFrame() pour initilaiser l'iframe d'interaction.
	 */
	interact(session: CidSession, step: CidStep): Promise<ICidStepResult>

	///** Edition des metas manquantes. */
	//editMetas(session: CidSession, metas: CidMeta[]): Promise<ICidUiResult>

	/**
	 * Demande l'ouverture d'une fenêtre d'attente pendant un traitement asynchrone.
	 * Le handler doit utiliser step.initFrame() pour initilaiser l'iframe d'interaction.
	 *
	 * @return null si le ICidUiHandler si la fenetre d'attente n'a pas été ouverte ou si la fin de l'étape en cours
	 *  n'a pas été atteinte. le systemWait devrait alors prendre la main pour attendre la fin de l'étape.
	 */
	uiWait(session: CidSession, step: CidStep): Promise<null | ICidStepResult>
}

export interface ICidStepResult {
	error?: ECidStepError
	errorReason?: string
	errorHttpCode?: number
	metas?: Dict<string>
}

export enum ECidSessionState {
	notStarted,
	uiProcessSel,
	authenticating,
	uiAuth,
	inStep,
	/** Session achevée avec succès. */
	ended,
	/** Abandonné par l'utilisateur au cours d'une interaction. */
	aborted,
	/** Echec au cours de l'exécution d'un étape. */
	failed
}

export type ECidStepError = 'abort' | 'authFailed' | 'serverFailed' | 'other';
type ECidFailedError = ECidStepError | 'noProcessFound';

interface Iisable {
	is?: string[]
}

/**
 * Session CID.
 */
export class CidSession {

	/** Etat de la session. */
	state: ECidSessionState;

	/** Evènement dispatché à chaque changement d'état et d'étape. */
	onStateChange: EventMgr<(session: this) => void>;

	/** Process sélectionné. */
	cidProcess: CidProcess;

	/** Transport sélectionné. */
	cidTransport: ICidTransport;

	/** Etape en cours. */
	currentStep: CidStep;

	/** Iframe en cours. */
	currentFrame: HTMLIFrameElement;

	/** Resolver de la promise en cours. */
	currentResolver: (resolve: ICidStepResult) => void;

	/** Metas modifiées au cours du processus. */
	metasOut?: Dict<string>;

	/** SessionProperties à renvoyer dans chaque requête. */
	sessionProps?: Dict<string>;

	/** En cas d'echec, mémoire de l'erreur survenue au cours de la session. */
	failedError?: ECidFailedError;
	failedReason?: string;
	failedHttpCode?: number;

	constructor(public config: OCidSessionConfig) {
		this.state = ECidSessionState.notStarted;
		this.sessionProps = {};
		this.metasOut = Object.assign({}, config.metasIn);
		this.onStateChange = new EventMgr();
	}

	/** Execute la session. */
	async doSession(): Promise<void> {
		try {
			if (this.state !== ECidSessionState.notStarted) throw Error("CidSession already started");
			//1ère étape : sel du process.
			if (this.config.process) {
				this.cidProcess = this.config.process;
			} else {
				const prcs = CID.filterIsFirst(this.config.processes || this.config.manifest.processes, this.config.isProcess);
				if (prcs.size > 1) {
					//Besoin d'une sélection par le user.
					this.changeState(ECidSessionState.uiProcessSel);
					this.cidProcess = await this.config.uiHandler.selectProcess(this, prcs);
					if (this.cidProcess == null) {
						this.changeState(ECidSessionState.aborted);
						return;
					} else {
						// @ts-ignore
						if (this.state === ECidSessionState.aborted) return; // annulation async
					}
				} else if (prcs.size === 1) {
					//1 process trouvé
					this.cidProcess = prcs.keys().next().value;
				} else {
					this.cidProcess = null;
				}
			}
			if (this.cidProcess == null) {
				this.onStepFailed('noProcessFound', "Aucun traitement compatible n'a pu être trouvé.");
				return;
			}

			//2ème étape : matching des foreignMetas avec les metas de ce process
			if (this.config.foreignMetas) {
				const metas = this.cidProcess.metas;
				for (const fm of this.config.foreignMetas) {
					for (const m of metas.values()) {
						if (CID.isMatch(fm.is, m.is)) {
							this.metasOut[m.name] = typeof fm.value === 'function' ? fm.value(this) : fm.value;
							break;
						}
					}
				}
			}

			//3ème étape : auth
			this.cidTransport = this.cidProcess.transport;
			this.changeState(ECidSessionState.authenticating);
			const authUrl = await this.cidTransport.needWebAuth(this);
			// @ts-ignore
			if (this.state === ECidSessionState.aborted) return; // annulation async
			if (authUrl) {
				this.changeState(ECidSessionState.uiAuth);
				if (await this.processResult(this.config.uiHandler.webAuth(this, authUrl))) return;
			}

			//Etapes suivantes: Steps du process.
			for (const step of this.cidProcess.steps) {
				this.currentStep = step;
				this.changeState(ECidSessionState.inStep);
				if (await this.processResult(step.doStep(this))) return;
			}

			//Fin
			this.changeState(ECidSessionState.ended);
		} catch (e) {
			this.changeState(ECidSessionState.failed);
			ERROR.report("Une erreur est survenue au cours de ce traitement", e);
			throw e;
		} finally {
			//cleanup
			if (this.currentFrame) removeInteractPending(this.currentFrame, this);
		}
	}

	/** Abandon de la session en cours. */
	abortSession() {
		if (!this.isClosed()) this.changeState(ECidSessionState.aborted);
	}

	isClosed(): boolean {
		return this.state === ECidSessionState.ended || this.state === ECidSessionState.failed || this.state === ECidSessionState.aborted;
	}

	/**
	 * Réception d'un msg d'une iframe de webAuth, interaction ou userWait.
	 *
	 * Généralement le contexte graphique implémentant ICidUiHandler n'appelle pas elle-même
	 * cette méthode : le message provenant de l'iframe est capté automatiquement (cf addInteractPending()).
	 *
	 * Mais si le contexte graphique permet de supprimer / fermer l'iframe de l'extérieur (iframe dans un dialogue...),
	 * alors il doit appeler cette méthode lui-même avec en paramètre :
	 * - si c'est un webAuth : {cidAuth:"failed"}
	 * - si c'est une interaction ou un userWait : {cidInteraction:"aborted"}
	 *
	 */
	onCidFrameMessage(datas: JWebAuthDatas | JInteractionDatas) {
		if (!removeInteractPending(this.currentFrame, this)) return;
		const resolver = this.currentResolver;
		if (!resolver) return;
		this.currentResolver = null;
		this.currentFrame = null;

		if (this.state === ECidSessionState.uiAuth) {
			//webAuth
			if (datas.cidAuth === "succeeded") {
				delete datas.cidAuth;
				resolver({metas: datas});
			} else {
				resolver({error: "authFailed"});
			}
		} else if (this.currentStep) {
			//interaction ou userWait
			if (datas.cidInteraction === "ended") {
				delete datas.cidInteraction;
				resolver({metas: datas});
			} else {
				resolver({error: "abort"});
			}
		}
	}

	/**
	 * Affectation de la UI frame pour l'étape en cours (interact, userWait ou webAuth).
	 * Retourne un Promise qui sera résolu à l'appel de onCidFrameMessage()
	 */
	_setCurrentFrame(frame: HTMLIFrameElement): Promise<ICidStepResult> {
		addInteractPending(frame, this);
		this.currentFrame = frame;
		return new Promise<ICidStepResult>((resolve, reject) => {
			this.currentResolver = resolve;
		});
	}

	/** Update les props (metas et sessionProps) de la session. */
	_updateProps(newProps: Dict<any>) {
		if (!newProps) return;
		const sessProps = this.cidTransport.sessionProps;
		for (const k in newProps) {
			let v = newProps[k];
			switch (typeof v) {
			case "boolean":
			case "number":
			case "bigint":
				v = v.toString();
				break;
			case "object":
				v = JSON.stringify(v);
				break;
			}
			if (this.sessionProps && sessProps && sessProps.has(k)) this.sessionProps[k] = v;
			else this.metasOut[k] = v;
		}
	}

	/** Traite un résultat de step et retourne true si fin de la session (car erreur). */
	protected async processResult(futureResult: Promise<ICidStepResult>): Promise<boolean> {
		let result: ICidStepResult;
		try {
			result = await futureResult;
		} catch (e) {
			result = {
				error: "serverFailed",
				errorReason: e,
			}
		}
		if (this.state === ECidSessionState.aborted) return true; //Session abandonnée entre temps.
		if (result.error) {
			if (result.error === "abort") {
				this.changeState(ECidSessionState.aborted);
			} else {
				this.onStepFailed(result.error, result.errorReason, result.errorHttpCode);
			}
			return true;
		}
		this._updateProps(result.metas);
		return false;
	}

	protected onStepFailed(error: ECidFailedError, reason: string, httpCode?: number) {
		this.failedError = error;
		this.failedReason = reason;
		this.failedHttpCode = httpCode;
		this.changeState(ECidSessionState.failed);
	}

	protected changeState(state: ECidSessionState) {
		this.state = state;
		this.onStateChange.emit(this);
	}
}

/**
 *
 */
export class CidManifest {

	manifestUrl: string;

	get label(): CidL10nStr {return this._label === undefined ? (this._label = parseLabel(this.def)) : this._label}

	protected _label: CidL10nStr;

	processes: CidProcess[] = [];

	transports: ICidTransport[] = [];

	constructor(url: string, public def: Element) {
		this.manifestUrl = url;
		if (!def || def.localName !== "manifest" || def.namespaceURI !== CID.CID_NS) throw Error("Invalid cid manifest: " + url);

		//Transports
		const transports = DOM.findLastChild(def, (n: Node): n is Element => DOM.IS_element(n) && n.localName === "transports" && n.namespaceURI === CID.CID_NS);
		if (!transports) throw Error("No transports definition found");
		for (let trsp = transports.firstElementChild; trsp; trsp = trsp.nextElementSibling) {
			if (trsp.localName === "webTransport" && trsp.namespaceURI === CID.CID_NS) {
				this.transports.push(new CidWebTransport(trsp));
			}
		}
		//Process
		for (let prc = def.firstElementChild; prc; prc = prc.nextElementSibling) {
			if (prc.localName === "process" && prc.namespaceURI === CID.CID_NS) {
				this.processes.push(new CidProcess(prc, this.getTransport(prc.getAttribute("ids"))));
			}
		}
	}

	getProcesses(...is: string[]): CidProcess[] {
		return Array.from(CID.filterIsFirst(this.processes, is));
	}

	getTransport(ids: string | null): ICidTransport | null {
		if (!ids || ids.length === 0) return this.transports[0];
		for (const id of ids.split(' ')) {
			const tr = this.transports.find((t) => t.id === id);
			if (tr) return tr;
		}
		return null;
	}

}

export interface ICidManifestPointer {
	cidManifest?: CidManifest;
}


export class CidProcess {

	is: string[];

	get label(): CidL10nStr {return this._label === undefined ? (this._label = parseLabel(this.def)) : this._label}

	protected _label: CidL10nStr;

	get steps(): CidStep[] {
		if (!this._steps) {
			this._steps = [];
			for (let step = this.def.firstElementChild; step; step = step.nextElementSibling) {
				if (step.namespaceURI !== CID.CID_NS) continue;
				if (step.localName === "exchange") this._steps.push(new CidExchangeStep(step));
				else if (step.localName === "interact") this._steps.push(new CidInteractiveStep(step));
				else if (step.localName === "upload") this._steps.push(new CidUploadStep(step));
			}
		}
		return this._steps
	}

	protected _steps: CidStep[];

	get metas(): Map<string, CidMetaDef> {
		if (!this._metas) {
			this._metas = new Map();
			for (let meta = this.def.firstElementChild; meta; meta = meta.nextElementSibling) {
				if (meta.namespaceURI !== CID.CID_NS) continue;
				if (meta.localName === "meta") this._metas.set(meta.getAttribute("name"), new CidMetaDef(meta));
			}
		}
		return this._metas;
	}

	protected _metas: Map<string, CidMetaDef>;

	constructor(public def: Element, public transport: ICidTransport) {
		if (!transport) throw Error("No transport found");
		this.is = parseIs(def);
	}

}

/** Définition d'une meta dans un process. */
export class CidMetaDef {
	is?: string[];

	get name(): string {return this.def.getAttribute("name")}

	/** Peut servir à éliminer une meta de la liste des metas à stocker pour un prochain appel à ce même process. */
	get isExcludeForRecall(): boolean {return this.def.getAttribute("forRecall") === "exclude"}

	constructor(public def: Element) {
		this.is = parseIs(def);
	}
}

/** Step d'un process ou step de webAuth. */
export abstract class CidStep {

	constructor(public def: Element) {}

	getUiStepDef(): Element {return this.def}

	abstract doStep(session: CidSession): Promise<ICidStepResult>

	/** Appelé par le contexte graphique pour initialiser sa frame et obtenir la Promise à retourner. */
	initFrame(frame: HTMLIFrameElement & ICidFrame, session: CidSession): Promise<ICidStepResult> {
		const uiStepDef = this.getUiStepDef();
		const urlObj = parseCidStepUrl(uiStepDef);
		const url = urlObj ? urlObj.getStr("fr") : null;
		const webInteract = session.cidTransport.webInteract;
		if (webInteract.POST_MULTIPART || webInteract.POST_URLENC) {
			//on passe par un formulaire POST pour pointer la frame target.
			if (!frame.name) frame.name = "cid" + Math.round(Math.random() * 10000000);
			const form = <form method="POST"
												 enctype={webInteract.POST_URLENC ? "application/x-www-form-urlencoded" : "multipart/form-data"}
												 target={frame.name}
												 action={url}/> as HTMLFormElement;
			CidStep.enrichForm(form, session, uiStepDef);
			form.submit();
		} else if (webInteract.GET) {
			//on passe en QS (header impossible)
			frame.src = CidStep.enrichUrl(url, session, uiStepDef);
		} else {
			throw Error("webInteract not allowed in this transport: " + DOM.debug(session.cidTransport.def));
		}
		return session._setCurrentFrame(frame);
	}

	static enrichUrl(url: string, session: CidSession, elt: Element): string {
		const qs = new UrlQs();
		this.enrichParams(qs, session, elt);
		if (url.indexOf('?') > 0) return url += "&" + qs.toString();
		else return url += "?" + qs.toString();
	}

	static enrichParams<T extends URLSearchParams | FormData>(p: T, session: CidSession, elt: Element, checkNeeds: boolean = true): T {
		for (const k in session.sessionProps) (p as any).append(k, session.sessionProps[k]);
		for (const k of metasUsesForStep(elt, session)) {
			const v = session.metasOut[k];
			if (v != null) (p as any).append(k, v);
		}
		let missingNeeds = [];
		for (const k of metasNeedsForStep(elt)) {
			const v = session.metasOut[k];
			if (v != null) (p as any).append(k, v);
			else
				missingNeeds.push(k);
		}
		if (checkNeeds && missingNeeds.length > 0)
			throw Error("Missing needs properties: " + missingNeeds.join(", "));
		return p;
	}

	static enrichForm(form: HTMLFormElement, session: CidSession, elt: Element, checkNeeds: boolean = true): void {
		for (const k in session.sessionProps) form.appendChild(<input type="hidden" name={k} value={session.sessionProps[k]}/>);
		for (const k of metasUsesForStep(elt, session)) {
			const v = session.metasOut[k];
			if (v != null) form.appendChild(<input type="hidden" name={k} value={v}/>)
		}
		let missingNeeds = [];
		for (const k of metasNeedsForStep(elt)) {
			const v = session.metasOut[k];
			if (v != null) form.appendChild(<input type="hidden" name={k} value={v}/>)
			else
				missingNeeds.push(k);
		}
		if (checkNeeds && missingNeeds.length > 0)
			throw Error("Missing needs properties: " + missingNeeds.join(", "));
	}

	static enrichHeaders(session: CidSession, elt: Element, checkNeeds: boolean = true): Dict<any> {
		const p: Dict<any> = {};
		for (const k in session.sessionProps) p[k] = session.sessionProps[k];
		for (const k of metasUsesForStep(elt, session)) {
			const v = session.metasOut[k];
			if (v != null) p[k] = v;
		}
		let missingNeeds = [];
		for (const k of metasNeedsForStep(elt)) {
			const v = session.metasOut[k];
			if (v != null) p[k] = v;
			else
				missingNeeds.push(k);
		}
		if (checkNeeds && missingNeeds.length > 0)
			throw Error("Missing needs properties: " + missingNeeds.join(", "));
		return p;
	}
}

export class CidWebAuthStep extends CidStep {

	doStep(session: CidSession): Promise<ICidStepResult> {
		return session.config.uiHandler.webAuth(session, this);
	}
}

export class CidInteractiveStep extends CidStep {

	doStep(session: CidSession): Promise<ICidStepResult> {
		return session.config.uiHandler.interact(session, this);
	}
}

export class CidExchangeStep extends CidStep {

	getUiStepDef(): Element {return DOM.findFirstChild(this.def, (n: Node): n is Element => DOM.IS_element(n) && n.localName === "userWait" && n.namespaceURI === CID.CID_NS)}

	getSystemWaitDef(): Element {return DOM.findFirstChild(this.def, (n: Node): n is Element => DOM.IS_element(n) && n.localName === "systemWait" && n.namespaceURI === CID.CID_NS)}

	async doStep(session: CidSession): Promise<ICidStepResult> {
		let resp = await this.doRequest(session);
		const text = await resp.text();
		if (!resp.ok) return {error: 'serverFailed', errorReason: text, errorHttpCode: resp.status};
		const metas = text ? JSON.parse(text) : null;
		session._updateProps(metas);
		while (resp.status === 202) {
			//Asynch
			const uiWait = this.getUiStepDef();
			if (uiWait) {
				const uiResult = await session.config.uiHandler.uiWait(session, this);
				if (uiResult) return uiResult;
			}
			const sysWait = this.getSystemWaitDef();
			if (sysWait) {
				await new Promise((resolve) => setTimeout(resolve, 3000));
				resp = await CidExchangeStep.doWebExchange(session, sysWait);
				if (!resp.ok) return {error: 'serverFailed', errorReason: text, errorHttpCode: resp.status};
				const metas = text ? JSON.parse(text) : null;
				session._updateProps(metas);
			}
		}
		return {};//Ok et pas de metas, traités à chaque remontée
	}

	doRequest(session: CidSession): Promise<Response> {
		return CidExchangeStep.doWebExchange(session, this.def);
	}

	static async doWebExchange(session: CidSession, stepDef: Element): Promise<Response> {
		const urlObj = parseCidStepUrl(stepDef);
		const url = urlObj ? urlObj.getStr("fr") : null;
		const webExch = session.cidTransport.webExchange;
		if (webExch.POST_URLENC || webExch.POST_MULTIPART) {
			const body = CidStep.enrichParams(webExch.POST_URLENC ? new UrlQs() : new FormData(), session, stepDef);
			const headers = webExch.POST_URLENC ? {"content-type": "application/x-www-form-urlencoded"} : {};
			return fetch(url, {method: "POST", body, headers, credentials: "include"});
		} else if (webExch.GET && webExch.GET.queryString) {
			return fetch(CidStep.enrichUrl(url, session, stepDef), {credentials: "include"});
		} else if (webExch.GET && webExch.GET.header) {
			return fetch(url, {headers: CidStep.enrichHeaders(session, stepDef), credentials: "include"});
		} else throw Error("webExchange properties method not found in transport: " + DOM.debug(session.cidTransport.def));
	}
}

export class CidUploadStep extends CidExchangeStep {

	get uploadUrl(): string {return this.def.getAttribute("url")}

	async doRequest(session: CidSession): Promise<Response> {
		const stepDef = this.def;
		let url = this.uploadUrl;
		const webUpload = session.cidTransport.webUpload;
		if (session.config.customUpload) {
			//Déploiement custom.
			const res = await session.config.customUpload(session, this, webUpload);
			if (res != null) return res;
		}
		//Déploiement standard avec session.config.bodyToUpload
		const init: RequestInit = {credentials: "include"};
		let formData: FormData;
		let propsMedia: ICidPropsMedia;
		if (webUpload.POST_MULTIPART) {
			propsMedia = webUpload.POST_MULTIPART;
			formData = new FormData();
			formData.append("cidContent", session.config.bodyToUpload as any); //XXX PAs sûr que FormData accepte
			init.method = "POST";
			init.body = formData;
		} else if (webUpload.PUT) {
			propsMedia = webUpload.PUT;
			init.method = "PUT";
			init.body = session.config.bodyToUpload;
		} else if (webUpload.POST) {
			propsMedia = webUpload.POST;
			init.method = "POST";
			init.body = session.config.bodyToUpload;
		} else throw Error("webUpload method not found in transport: " + DOM.debug(session.cidTransport.def));

		if (propsMedia.post) CidStep.enrichParams(formData, session, stepDef);
		else if (propsMedia.queryString) url = CidStep.enrichUrl(url, session, stepDef);
		else if (propsMedia.header) init.headers = CidStep.enrichHeaders(session, stepDef);
		else throw Error("webUpload properties media not found in transport: " + DOM.debug(session.cidTransport.def));

		init.credentials = "include";

		return fetch(url, init);
	}
}

/** Itère sur les metas "use"" à envoyer */
function* metasUsesForStep(elt: Element, cidSession: CidSession) {
	const use = elt.getAttribute("useMetas");
	if (use) {
		const keys = use.split(" ");
		for (let k of keys) {
			if (k === "*") {
				//On retourne toutes les metasOut qui ne sont pas déclarées
				const knownMetas = cidSession.cidProcess.metas;
				const values = cidSession.metasOut;
				for (let outKey in values) {
					if (!knownMetas.has(outKey)) yield outKey;
				}
			} else {
				yield k;
			}
		}
	}
}

/** Itère sur les metas "need" à envoyer */
function* metasNeedsForStep(elt: Element) {
	const need = elt.getAttribute("needMetas");
	if (need) yield* need.split(" ");
}

/** */
interface ICidTransport {
	id: string | null
	def: Element
	sessionProps: Set<string> | null
	webExchange: {
		GET?: ICidPropsMedia
		POST_URLENC?: ICidPropsMedia
		POST_MULTIPART?: ICidPropsMedia
	}
	webInteract: {
		GET?: ICidPropsMedia
		POST_URLENC?: ICidPropsMedia
		POST_MULTIPART?: ICidPropsMedia
	}
	webUpload: ICidWebUploadDef

	/** Evalue si une webAuth est nécessaire, si oui, retourne la CidWebAuthStep correspondante. */
	needWebAuth(session: CidSession): Promise<null | CidWebAuthStep>

}

export interface ICidWebUploadDef {
	//GET?: boolean XXX body interdit en GET non ?
	PUT?: ICidPropsMedia
	POST?: ICidPropsMedia
	POST_MULTIPART?: ICidPropsMedia
}

/** Zone de stockage des props dans un webTransport. */
interface ICidPropsMedia {
	header?: boolean
	queryString?: boolean
	post?: boolean
}

class CidWebTransport implements ICidTransport {

	get id(): string {return this.def.getAttribute('id')}

	sessionProps: Set<string> | null;
	webExchange = {};
	webInteract = {};
	webUpload = {};


	constructor(public def: Element) {
		const props = def.getAttribute("sessionProperties");
		if (props) this.sessionProps = new Set(props.split(' '));
		for (let ch = def.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (ch.namespaceURI !== CID.CID_NS) continue;
			const webReq = (this as any)[ch.localName];
			if (webReq) {
				for (let req = ch.firstElementChild; req; req = req.nextElementSibling) {
					const m = req.getAttribute("method");
					if (m === "GET") webReq.GET = getPropsMedia(req);
					else if (m === "PUT") webReq.PUT = getPropsMedia(req);
					else if (m === "POST") webReq.POST = getPropsMedia(req);
					else if (m === "POST;application/x-www-form-urlencoded") webReq.POST_URLENC = getPropsMedia(req);
					else if (m === "POST;multipart/form-data") webReq.POST_MULTIPART = getPropsMedia(req);
				}
			}
		}

		function getPropsMedia(req: Element): ICidPropsMedia {
			const r = {} as any;
			for (const p of req.getAttribute("properties").split(' ')) if (p) r[p] = true;
			return r;
		}
	}

	async needWebAuth(session: CidSession): Promise<null | CidWebAuthStep> {
		const auths = DOM.findFirstChild(this.def, (n: Node): n is Element => DOM.IS_element(n) && n.localName === "authentications" && n.namespaceURI === CID.CID_NS);
		if (!auths) return null; //pas d'auth
		let webAuthStep: CidWebAuthStep = null;
		for (let ch = auths.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (ch.namespaceURI !== CID.CID_NS) continue;
			if (ch.localName === "basicAuth") {
				if (await this.checkAuth(session, ch)) return null; //authBasic Ok.
			} else if (ch.localName === "webAuthentication") {
				if (await this.checkAuth(session, ch)) return null; //authWeb Ok
				webAuthStep = new CidWebAuthStep(ch);
			} else if (ch.localName === "noAuthentication") {
				return null; //si noAuth est présent on n'impose rien.
			}
		}
		return webAuthStep;
	}

	protected async checkAuth(session: CidSession, auth: Element): Promise<boolean> {
		for (let ch = auth.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (ch.localName === "checkAuth" && ch.namespaceURI === CID.CID_NS) {
				try {
					const resp = await CidExchangeStep.doWebExchange(session, ch);
					return resp.ok;
				} catch (e) {
					return false;
				}
			}
		}
		return false;
	}
}


class CidL10nStr extends Map<string, string> {

	default: string;

	set(key: string, value: string): this {
		if (this.size === 0) this.default = value;
		super.set(key, value);
		let frag = key.indexOf('-');
		while (frag > 0) {
			super.set(key.substring(0, frag), value);
			frag = key.indexOf('-', frag + 1);
		}
		return this;
	}

	/** Retourne la chaine de caractère dans la langue voulue */
	getStr(...lang: string[]): string {
		if (lang) {
			//Match exact
			for (const lg of lang) {
				const label = this.get(lg);
				if (label != undefined) return label;
			}
			//Match partiel
			for (const lg of lang) {
				let frag = lg.lastIndexOf('-');
				while (frag > 0) {
					const label = this.get(lg.substring(frag + 1));
					if (label != undefined) return label;
					frag = lg.lastIndexOf('-', frag - 1);
				}
				const label = this.get(lg);
				if (label != undefined) return label;
			}
		}
		return this.default || "";
	}
}

function parseIs(elt: Element): string[] {
	const att = elt.getAttribute("is");
	if (!att) return null;
	return att.trim().split(/ +/);
}

function parseLabel(parent: Element): CidL10nStr | null {
	const label: CidL10nStr = new CidL10nStr();
	for (let lb = parent.firstElementChild; lb; lb = lb.nextElementSibling) {
		if (lb.localName === "label" && lb.namespaceURI === CID.CID_NS) {
			label.set(lb.getAttribute("xml:lang"), lb.textContent);
		}
	}
	return label;
}

/** tags fils url localisés, ou @url à défaut */
export function parseCidStepUrl(parent: Element): CidL10nStr | null {
	const url: CidL10nStr = new CidL10nStr();
	for (let lb = parent.firstElementChild; lb; lb = lb.nextElementSibling) {
		if (lb.localName === "url" && lb.namespaceURI === CID.CID_NS) {
			url.set(lb.getAttribute("xml:lang"), lb.textContent);
		}
	}
	if (url.size === 0 && parent.hasAttribute('url'))
		url.set('zxx', parent.getAttribute('url'));
	return url.size > 0 ? url : null;
}

//const frameMsgLstnKey = Symbol("CidFrameMsgListenerKey");

type ICidFrame = {
	//[frameMsgLstnKey]: (ev: MessageEvent) => void
}

/** Données de retour d'une iframe de webAuth*/
type JWebAuthDatas = {
	cidAuth: 'succeeded' | 'failed'
	/* Metas or session properties. */
	[meta: string]: any
}

/** Données de retour d'une iframe d'interaction*/
export interface JInteractionDatas {
	cidInteraction: 'ended' | 'aborted'

	/* Metas or session properties. */
	[meta: string]: any
}


/** Map des interactions en cours (plusieurs sessions peuvent s'exécuter en //). */
let interactPending: Map<HTMLIFrameElement, CidSession>;

function addInteractPending(frame: HTMLIFrameElement, session: CidSession) {
	if (!interactPending) {
		//1er appel
		interactPending = new Map();
		window.addEventListener("message", function (ev: MessageEvent) {
			for (const [key, val] of interactPending.entries()) {
				if (key.contentWindow === ev.source) {
					val.onCidFrameMessage(ev.data);
					break;
				} else if (key.contentWindow == null) {
					interactPending.delete(key); //iframe supprimée, cleanup
				}
			}
		})
	}
	interactPending.set(frame, session);
}

/** Retourne true si cette session était bien en attente de cette frame. */
function removeInteractPending(frame: HTMLIFrameElement, session: CidSession): boolean {
	if (!interactPending) return false;
	const sess = interactPending.get(frame);
	if (sess === session) {
		interactPending.delete(frame);
		return true;
	}
	return false;
}


export class CidHost {
	is: string[];
	use: 'input' | 'output' | 'both' = "both";
	label: string;
	manifestUrl: string;

	/** */
	manifest: CidManifest | null | undefined;

	constructor(def?: Element) {
		if (def != null) {
			this.label = def.getAttribute("label");
			this.manifestUrl = def.getAttribute("manifestUrl");
			this.is = parseIs(def);
			this.use = def.getAttribute("use") as any || 'both';
			const mn = def.firstElementChild;
			if (mn && mn.localName === "manifest") {
				try {
					this.manifest = new CidManifest(this.manifestUrl, mn);
				} catch (e) {
					this.manifest = null;
				}
			}
		}
	}

	async fetchManifest(): Promise<CidManifest> {
		if (this.manifest === undefined) {
			try {
				this.manifest = await CID.getCidManifest(this.manifestUrl);
			} catch (e) {
				this.manifest = null;
			}
		}
		return this.manifest;
	}

	static createFromUrl(url: string, use?: 'input' | 'output' | 'both', label?: string): CidHost {
		const cidHost = new CidHost();
		cidHost.manifestUrl = url;
		if (use) cidHost.use = use;
		if (label) cidHost.label = label;
		return cidHost;
	}
}

type ICidHostsPointer = { cidHosts?: CidHost[] }

export namespace CID {

	export async function getCidManifest(path: string): Promise<CidManifest> {
		const url = IO.absoluteUrl(path);
		const headers = new Headers();
		headers.set("X-CidManifest", "1");
		const resp = await fetch(url, {headers});
		if (!resp.ok) throw resp;
		return new CidManifest(url, DOM.parseDomValid(await resp.text()).documentElement);
	}

	export async function getCidHosts(reg: IReg<IUniverseEnv>): Promise<CidHost[]> {
		const univ: Universe & ICidHostsPointer = reg.env.universe;
		if (!univ.cidHosts) {
			const doc = await univ.config.httpFrames.web.execFrameUrl.fetchDom("u/cidProvider/GetManifests");
			univ.cidHosts = [];
			if (doc) for (let ch = doc.documentElement.firstElementChild; ch; ch = ch.nextElementSibling) {
				if (ch.localName === "cidHost") univ.cidHosts.push(new CidHost(ch));
			}
		}
		return univ.cidHosts;
	}

	export async function getCidHostsForInput(reg: IReg<IUniverseEnv>): Promise<CidHost[]> {
		const list = await getCidHosts(reg);
		return list.filter((ch: CidHost) => ch.use !== "output");
	}

	export async function getCidHostsForOutput(reg: IReg<IUniverseEnv>): Promise<CidHost[]> {
		const list = await getCidHosts(reg);
		return list.filter((ch: CidHost) => ch.use !== "input");
	}

	export function getCidManifestLabel(hosts: CidHost[], manifest: CidManifest): string {
		if (hosts) {
			const h = hosts.find((h) => h.manifestUrl === manifest.manifestUrl);
			if (h) return h.label;
		}
		const label = manifest.label.getStr("fr");
		return label || manifest.manifestUrl;
	}

	/**
	 * Retourne tous les objets matchant n'importe quelle entrée de isList
	 * triés dans l'ordre des isList qui matchent puis de celui du tableau objects.
	 * Retourne le tableau entier des objects si isList est null ou vide.
	 *
	 * A utiliser quand isList est une liste d'identifiant ou de termes non hiérarchisés.
	 * @see filterIsFirst()
	 */
	export function filterIs<T extends Iisable>(objects: T[] | null, isList: string[] | null): Set<T> {
		if (!isList || isList.length === 0 || !objects) return new Set(objects);
		const result: Set<T> = new Set();
		for (const is of isList) {
			objects.filter((p: T) => p.is && p.is.indexOf(is) >= 0).forEach((p: T) => result.add(p));
		}
		return result;
	}

	/**
	 * Retourne les objets matchant le 1er terme de isList, à défaut le 2ème terme de isList, etc.
	 * Retourne le tableau entier des objects si isList est null ou vide.
	 *
	 * A utiliser quand isList est une liste hiérarchisée de termes sémantiques (du plus précis au plus générique).
	 * @see filterIs()
	 */
	export function filterIsFirst<T extends Iisable>(objects: T[] | null, isList: string[] | null, addIn?: Set<T>): Set<T> {
		if (!isList || isList.length === 0) {
			if (!objects) return addIn || new Set();
			if (!addIn) return new Set(objects);
			objects.forEach(addIn.add, addIn);
			return addIn;
		}
		const result: Set<T> = addIn || new Set();
		for (const is of isList) {
			objects.filter((p: T) => p.is && p.is.indexOf(is) >= 0).forEach((p: T) => result.add(p));
			if (result.size > 0) return result; //Ce is a matché au moins une fois, on retourne cette sous-liste préférentielle.
		}
		return result;
	}

	/**
	 * Equivalent à CID.filterIs() si isList est de type string[]
	 * Mais si isList est de type string[][] c'est un OR qui est appliqué entre
	 * chaque série hiérarchisée de termes string[] via CID.filterIsFirst().
	 * @see filterIs()
	 * @see filterIsFirst()
	 */
	export function filterIsOr<T extends Iisable>(objects: T[] | null, isList: string[] | string[][] | null): Set<T> {
		if (!isList || isList.length === 0 || !objects) return new Set(objects);
		if (typeof isList[0] === "string") return filterIs(objects, isList as string[]);
		const result: Set<T> = new Set();
		for (const is of isList) filterIsFirst(objects, is as string[], result);
		return result;
	}

	export function isMatch(is1: string[] | null, is2: string[] | null): boolean {
		if (!is1 || !is2) return false;
		for (const k1 of is1) if (is2.indexOf(k1) >= 0) return true;
		return false;
	}

	/** Retourne une forme lisible d'une erreur CID */
	export function cidErrorToMsg(cidError: ECidStepError | ECidFailedError) {
		switch (cidError) {
		case "abort":
			return "Processus abandonné"
		case "authFailed" :
			return "Échec d'authentification"
		case "serverFailed":
			return "Échec de traitement de la demande par le système cible"
		case "noProcessFound" :
			return "Aucun traitement compatible n'a pu être trouvé"
		case "other" :
			return "Erreur indéterminée"
		}
	}

	/** "is" à utiliser pour les foreignMetas de type "fileName". */
	export const IS_FileName = ["http://schema.org/name", "http://purl.org/dc/terms/title"];

	/** "is" à utiliser pour les foreignMetas de type "contentType". */
	export const IS_ContentType = ["http://purl.org/dc/terms/format", "http://schema.org/DataType"];

	/** "is" à utiliser pour les foreignMetas de type "réenvoi aveugle"
	 * 		idéalement, nécessité donc des contrôles supplémentaires (processing, existence de la res, ...)*/
	export const IS_Resend = ["scenari.eu:cid:resend"];

	export const CID_NS = "http://www.cid-protocol.org/schema/v1/core";
}
